import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SEOHead from '@/components/feature/SEOHead';
import { aboutContent, faqs } from '@/mocks/siteContent';

export default function AboutPage() {
  const [contactForm, setContactForm] = useState({
    name: '',
    email: '',
    message: '',
    website_alt: '',
  });
  const [formStatus, setFormStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');
  const [faqOpenIndex, setFaqOpenIndex] = useState<number | null>(null);

  const toggleFAQ = (index: number) => {
    setFaqOpenIndex(faqOpenIndex === index ? null : index);
  };

  const handleContactSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formEl = e.currentTarget;

    // Honeypot check
    const honeypotValue = (formEl.querySelector('[name="website_alt"]') as HTMLInputElement)?.value?.trim();
    if (honeypotValue) {
      setFormStatus('success');
      setContactForm({ name: '', email: '', message: '', website_alt: '' });
      setTimeout(() => setFormStatus('idle'), 3000);
      return;
    }

    setFormStatus('loading');
    setFormError('');

    try {
      const formData = new FormData(formEl);
      // Remove honeypot
      formData.delete('website_alt');

      const submitUrl = 'https://readdy.ai/api/form/d9mtt7gbdkpk99j7ge1g';
      const response = await fetch(submitUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });

      const responseText = await response.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        setContactForm({ name: '', email: '', message: '', website_alt: '' });
        setTimeout(() => setFormStatus('idle'), 3000);
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || responseText || '提交失败，请稍后重试';
        setFormError(serverMsg);
        setFormStatus('error');
      }
    } catch {
      setFormError('网络错误，请检查网络后重试');
      setFormStatus('error');
    }
  };

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    'name': '关于我们 - 蘑菇视频',
    'description': '了解蘑菇视频的品牌故事、口碑评价和联系方式。蘑菇视频聚合优质影视资源，提供免费高清在线观看体验。',
    'url': 'https://mogushipin.com/about',
    'mainEntity': {
      '@type': 'Organization',
      'name': '蘑菇视频',
      'url': 'https://mogushipin.com',
      'description': '蘑菇视频聚合西瓜视频免费、蜜桃视频等平台，提供高清电影电视剧免费在线观看。',
      'contactPoint': {
        '@type': 'ContactPoint',
        'telephone': '+86-400-888-6666',
        'contactType': 'customer service',
        'email': 'contact@mogushipin.com',
        'availableLanguage': ['Chinese'],
      },
    },
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    'mainEntity': faqs.map((faq) => ({
      '@type': 'Question',
      'name': faq.question,
      'acceptedAnswer': {
        '@type': 'Answer',
        'text': faq.answer,
      },
    })),
  };

  return (
    <>
      <SEOHead
        title="关于我们 - 蘑菇视频 | 品牌介绍 口碑评价 联系方式"
        description="了解蘑菇视频的品牌故事、口碑评价和联系方式。蘑菇视频聚合西瓜视频免费、蜜桃视频等平台，提供高清电影电视剧免费在线观看，无广告流畅播放。"
        keywords="蘑菇视频,关于我们,品牌介绍,联系方式,影视平台"
        canonicalUrl="https://mogushipin.com/about"
        schema={schema}
      />

      {/* FAQ schema */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
      />

      <Navbar />

      <main className="pt-20 pb-10 min-h-screen bg-background-50">
        <div className="max-w-4xl mx-auto px-4 md:px-6">
          <Breadcrumb items={[{ name: '首页', path: '/' }, { name: '关于我们' }]} />

          {/* Brand Intro */}
          <section className="mb-14">
            <h1 className="text-2xl md:text-4xl font-heading font-bold text-foreground-900 mb-6">
              {aboutContent.brandIntro.title}
            </h1>
            <div className="flex flex-col lg:flex-row gap-8">
              <div className="flex-1">
                {aboutContent.brandIntro.content.split('\n\n').map((p, idx) => (
                  <p key={idx} className="text-foreground-700 leading-relaxed mb-4">
                    {p.trim()}
                  </p>
                ))}
              </div>
              <div className="w-full lg:w-80 flex-shrink-0">
                <div className="bg-background-100 rounded-xl p-6">
                  <div className="flex flex-col items-center text-center">
                    <div className="w-20 h-20 flex items-center justify-center bg-primary-500 rounded-2xl mb-4">
                      <i className="ri-play-circle-fill text-background-50 text-4xl"></i>
                    </div>
                    <h4 className="text-lg font-heading font-bold text-foreground-900 mb-1">蘑菇视频</h4>
                    <p className="text-sm text-foreground-500 mb-4">免费高清在线视频平台</p>
                  </div>
                  <div className="space-y-3">
                    <div className="flex items-center gap-3 text-sm">
                      <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-primary-50 text-primary-500 flex-shrink-0">
                        <i className="ri-user-star-line"></i>
                      </div>
                      <span className="text-foreground-700">5000万+ 累计用户</span>
                    </div>
                    <div className="flex items-center gap-3 text-sm">
                      <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-accent-50 text-accent-500 flex-shrink-0">
                        <i className="ri-vidicon-line"></i>
                      </div>
                      <span className="text-foreground-700">10万+ 影视资源</span>
                    </div>
                    <div className="flex items-center gap-3 text-sm">
                      <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-secondary-50 text-secondary-500 flex-shrink-0">
                        <i className="ri-shield-check-line"></i>
                      </div>
                      <span className="text-foreground-700">0广告 纯净体验</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Reputation */}
          <section className="mb-14">
            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-6">
              {aboutContent.reputation.title}
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {aboutContent.reputation.items.map((item, idx) => (
                <div key={idx} className="bg-background-100 rounded-xl p-5 flex flex-col">
                  <div className="flex items-center gap-1 mb-3">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <i
                        key={i}
                        className={`text-sm ${i < item.rating ? 'ri-star-fill text-accent-500' : 'ri-star-line text-foreground-300'}`}
                      ></i>
                    ))}
                  </div>
                  <p className="text-sm text-foreground-700 leading-relaxed mb-3 flex-1">
                    &ldquo;{item.quote}&rdquo;
                  </p>
                  <p className="text-xs font-medium text-foreground-500">
                    &mdash; {item.name}
                  </p>
                </div>
              ))}
            </div>
          </section>

          {/* Contact */}
          <section className="mb-14">
            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-6">
              {aboutContent.contact.title}
            </h2>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Contact Info */}
              <div className="space-y-4">
                <div className="flex items-start gap-4 p-4 bg-background-100 rounded-xl">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-50 text-primary-500 flex-shrink-0">
                    <i className="ri-mail-line text-lg"></i>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-foreground-900 mb-0.5">邮箱</p>
                    <a href={`mailto:${aboutContent.contact.email}`} className="text-sm text-foreground-600 hover:text-primary-500 transition-colors">
                      {aboutContent.contact.email}
                    </a>
                  </div>
                </div>
                <div className="flex items-start gap-4 p-4 bg-background-100 rounded-xl">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-accent-50 text-accent-500 flex-shrink-0">
                    <i className="ri-phone-line text-lg"></i>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-foreground-900 mb-0.5">电话</p>
                    <a href={`tel:${aboutContent.contact.phone}`} className="text-sm text-foreground-600 hover:text-primary-500 transition-colors">
                      {aboutContent.contact.phone}
                    </a>
                  </div>
                </div>
                <div className="flex items-start gap-4 p-4 bg-background-100 rounded-xl">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-secondary-50 text-secondary-500 flex-shrink-0">
                    <i className="ri-map-pin-line text-lg"></i>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-foreground-900 mb-0.5">地址</p>
                    <p className="text-sm text-foreground-600">{aboutContent.contact.address}</p>
                  </div>
                </div>
                <div className="flex items-start gap-4 p-4 bg-background-100 rounded-xl">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-50 text-primary-500 flex-shrink-0">
                    <i className="ri-time-line text-lg"></i>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-foreground-900 mb-0.5">工作时间</p>
                    <p className="text-sm text-foreground-600">{aboutContent.contact.workHours}</p>
                  </div>
                </div>
              </div>

              {/* Contact Form */}
              <div className="bg-background-100 rounded-xl p-6">
                <h3 className="text-lg font-heading font-bold text-foreground-900 mb-1">发送消息</h3>
                <p className="text-sm text-foreground-500 mb-5">如有任何问题或建议，欢迎给我们留言</p>

                <form onSubmit={handleContactSubmit} data-readdy-form className="space-y-4">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-foreground-700 mb-1.5">姓名</label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={contactForm.name}
                      onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })}
                      required
                      className="w-full px-4 py-2.5 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 transition-colors"
                      placeholder="请输入您的姓名"
                    />
                  </div>
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-foreground-700 mb-1.5">邮箱</label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={contactForm.email}
                      onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
                      required
                      className="w-full px-4 py-2.5 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 transition-colors"
                      placeholder="请输入您的邮箱"
                    />
                  </div>
                  <div>
                    <label htmlFor="message" className="block text-sm font-medium text-foreground-700 mb-1.5">留言</label>
                    <textarea
                      id="message"
                      name="message"
                      value={contactForm.message}
                      onChange={(e) => setContactForm({ ...contactForm, message: e.target.value })}
                      required
                      maxLength={500}
                      rows={4}
                      className="w-full px-4 py-2.5 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 transition-colors resize-none"
                      placeholder="请输入您的留言内容（500字以内）"
                    ></textarea>
                    <p className="text-xs text-foreground-400 mt-1">{contactForm.message.length}/500</p>
                  </div>

                  {/* Honeypot */}
                  <input
                    type="text"
                    name="website_alt"
                    value={contactForm.website_alt}
                    onChange={(e) => setContactForm({ ...contactForm, website_alt: e.target.value })}
                    tabIndex={-1}
                    autoComplete="off"
                    readOnly
                    aria-hidden="true"
                    className="honeypot-field"
                  />

                  <button
                    type="submit"
                    disabled={formStatus === 'loading'}
                    className="w-full py-3 bg-primary-500 hover:bg-primary-600 disabled:bg-primary-400 text-background-50 font-medium rounded-lg text-sm transition-all duration-200 whitespace-nowrap"
                  >
                    {formStatus === 'loading' ? (
                      <span className="flex items-center justify-center gap-2">
                        <i className="ri-loader-4-line animate-spin"></i>
                        发送中...
                      </span>
                    ) : '发送消息'}
                  </button>

                  {formStatus === 'success' && (
                    <p className="text-sm text-accent-600 text-center">消息已成功发送，我们会尽快回复您！</p>
                  )}
                  {formStatus === 'error' && (
                    <p className="text-sm text-primary-500 text-center">{formError}</p>
                  )}
                </form>
              </div>
            </div>
          </section>

          {/* FAQ */}
          <section>
            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-6 text-center">
              常见问题
            </h2>
            <div className="max-w-2xl mx-auto space-y-3">
              {faqs.map((faq, index) => (
                <div key={index} className="bg-background-100 rounded-xl border border-background-200 overflow-hidden">
                  <button
                    onClick={() => toggleFAQ(index)}
                    className="w-full flex items-center justify-between px-5 py-4 text-left transition-colors hover:bg-background-50/80"
                  >
                    <span className="text-sm font-medium text-foreground-900 pr-4">{faq.question}</span>
                    <div className={`w-6 h-6 flex items-center justify-center flex-shrink-0 rounded-full transition-all duration-200 ${
                      faqOpenIndex === index ? 'bg-primary-500 text-background-50 rotate-45' : 'bg-background-200 text-foreground-500'
                    }`}>
                      <i className="ri-add-line text-sm"></i>
                    </div>
                  </button>
                  {faqOpenIndex === index && (
                    <div className="px-5 pb-4">
                      <p className="text-sm text-foreground-600 leading-relaxed">{faq.answer}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </section>
        </div>
      </main>

      <Footer />
    </>
  );
}